import React, { useContext, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { loadPreference, savePreference, UserPreferenceCodes } from "requests/preferences";

export const SpacingContext = React.createContext(null);

export interface SpacingDefinition {
  code: string;
  iconName: string;
  className: string;
  displayName: string;
}

export const SpacingDefinitions: SpacingDefinition[] = [
  { code: "compact", iconName: "density_small", className: "compact-spacing", displayName: "Compact" },
  { code: "spacious", iconName: "density_medium", className: "", displayName: "Spacious" },
];

const defaultSpacing = SpacingDefinitions.find(sd => sd.code === "compact");

export interface SpacingProviderValue {
  changeSpacing: (newSpacingCode: string) => void;
  currentSpacing: SpacingDefinition;
  spacingOptions: SpacingDefinition[];
}

const SpacingProvider = ({ children, internetUserId }) => {
  const [currentSpacing, setCurrentSpacing] = useState(defaultSpacing);
  const mounted = useRef(true);

  useEffect(() => {
    if (currentSpacing.className.length) document.body.classList.add(currentSpacing.className);

    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (internetUserId) {
      loadPreference(UserPreferenceCodes.muellerEnterpriseSpacing, preference => {
        if (preference.value) changeSpacing(preference.value);
      }, mounted);
    }
  }, [internetUserId]);

  const changeSpacing = (newSpacingCode: string) => {
    if (currentSpacing.className.length) document.body.classList.remove(currentSpacing.className);
    let newSpacing = Object.values(SpacingDefinitions).find(s => s.code === newSpacingCode) || defaultSpacing;
    if (newSpacing.className.length) document.body.classList.add(newSpacing.className);
    setCurrentSpacing(newSpacing);
    savePreference(UserPreferenceCodes.muellerEnterpriseSpacing, newSpacingCode, null, mounted);
  };

  (window as any).changeSpacing = changeSpacing;

  const providerValue: SpacingProviderValue = {
    changeSpacing,
    currentSpacing,
    spacingOptions: SpacingDefinitions,
  };

  return <SpacingContext.Provider value={providerValue}>{children}</SpacingContext.Provider>;
};

const mapStateToProps = state => ({
  internetUserId: state.user.profile.internetUserId,
});

export default connect(mapStateToProps)(SpacingProvider);

/** Provides spacing context to a class component. Do not use this in functional components because they can just use the context */
export const withSpacing = (WrappedComponent: React.ComponentType) => {
  return (props) => {
    const spacingContext = useContext(SpacingContext);
    return <WrappedComponent {...props} spacingContext={spacingContext} />;
  };
};
